import AuditInfo from 'components/audit/AuditInfo';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import MatterIndicators from 'components/matters/MatterIndicators';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import { FormattedDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { getCaseChips, getLinkChips } from 'pages/matters/util';
import React, { useContext, useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { Effort, EffortPartyRoleType, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { FeatureFlag } from 'types/features';
import { getClassifierText, t } from 'utils/getTranslations';
import { NamedEntityParts } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { sortByField } from 'utils/sort';
import { secondsToHours } from 'utils/time';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { nominationName } from '../../rankings-referees/nominationName';
import { NominationStatusControl } from '../../rankings-referees/NominationStatusControl';
import {
    NOMINATION_WARNINGS_PERMISSIONS,
    NominationWarningNote,
    useNominationWarnings,
    useNominationWarningText,
} from '../../rankings-referees/nominationWarnings';
import { EffortSection } from '../form-sections/EffortSection';

const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

const STATUS_PERMISSIONS = ['UPDATE.RANKINGS_REFEREE_STATUSES.ALL'];

interface Props {
    effort: Effort;
    classifiers: Record<string, ClassifierData>;
    /** The new design: each block is a section edited in place, and the expenses get their own. */
    isNew?: boolean;
}

const linkifyParties = (
    items: { party: { id: number; name: string } }[] | null | undefined,
    base: string
): React.ReactNode => {
    if (!items || items.length === 0) return '-';
    return (
        <div className="flex flex-col">
            {items.map(({ party }) => (
                <RecordLink className={LINK_CLASS} key={party.id} to={`/${base}/${party.id}`} label={party.name} />
            ))}
        </div>
    );
};

const linkifyMatters = (
    matters: { matter: { id: number; fullName: string; isConfidential?: boolean | null } }[] | null | undefined
): React.ReactNode => {
    if (!matters || matters.length === 0) return '-';
    return (
        <div className="flex flex-col">
            {matters.map(({ matter }) => (
                <span key={matter.id} className="flex items-center gap-1">
                    <MatterIndicators isConfidential={matter.isConfidential} />
                    <RecordLink className={LINK_CLASS} to={`/matter/${matter.id}`} label={matter.fullName} />
                </span>
            ))}
        </div>
    );
};

const linkifyEmployees = (
    items: { employee: NamedEntityParts & { id: number } }[] | null | undefined
): React.ReactNode => {
    if (!items || items.length === 0) return '-';
    return (
        <div className="flex flex-col">
            {items.map(({ employee }) => (
                <EmployeeNameLink
                    className={LINK_CLASS}
                    key={employee.id}
                    to={`/employee/${employee.id}`}
                    entity={employee}
                />
            ))}
        </div>
    );
};

const GeneralView: React.FC<Props> = ({ effort, classifiers, isNew = false }) => {
    // On a draft, the values it was saved without read as missing, and whom it is shared with shows.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    const sharedWith = draftActions ? <DraftSharedWith /> : null;
    const forPersons = useMemo(
        () => (effort.parties ?? []).filter((p) => p.effortPartyRoleTypeCode === EffortPartyRoleType.FOR_PERSONS),
        [effort.parties]
    );
    const forEntities = useMemo(
        () => (effort.parties ?? []).filter((p) => p.effortPartyRoleTypeCode === EffortPartyRoleType.FOR_ENTITIES),
        [effort.parties]
    );

    const overviewRows: Row[] = useMemo(
        () => [
            {
                label: t('efforts.effort-date'),
                content: effort.effortDate || '-',
            },
            {
                label: t('efforts.activity-type'),
                content: effort.effortActivityTypeCode
                    ? getClassifierText(classifiers['EFFORT_ACTIVITY_TYPE'], effort.effortActivityTypeCode) || '-'
                    : '-',
            },
            {
                label: t('efforts.used-time'),
                content: <HoursDisplay value={secondsToHours(effort.usedTimeInSeconds ?? 0, 2)} />,
            },
            {
                label: t('efforts.description'),
                content: effort.description || '-',
                multiline: true,
            },
        ],
        [effort, classifiers]
    );
    // The new design's cards list their rows as the section's form lists its fields.
    const [effortDate, activityType, usedTime, description] = overviewRows;
    const newOverviewRows: Row[] = [
        { ...effortDate, content: <FormattedDate value={effort.effortDate} /> },
        usedTime,
        activityType,
        { ...description, label: infoLabel('efforts.description', 'efforts.tooltips.description') },
    ];

    const linkedEntitiesRows: Row[] = useMemo(
        () => [
            {
                label: t('efforts.resulted-in-cases'),
                content: linkifyMatters(effort.matters),
            },
            {
                label: t('efforts.for-persons'),
                content: linkifyParties(forPersons, 'contact'),
            },
            {
                label: t('efforts.for-entities'),
                content: linkifyParties(forEntities, 'contact'),
            },
        ],
        [effort.matters, forPersons, forEntities]
    );
    const [resultedInCases, effortForPersons, effortForEntities] = linkedEntitiesRows;
    const newLinkedEntitiesRows: Row[] = [
        effortForPersons,
        effortForEntities,
        {
            ...resultedInCases,
            content: '-',
            // One chip a case, as the field has them, each still the case's link.
            chips: getCaseChips((effort.matters ?? []).map(({ matter }) => matter)),
        },
    ];

    const peopleRows: Row[] = useMemo(
        () => [
            {
                label: t('efforts.effort-by-employee'),
                content: effort.effortByEmployee?.id ? (
                    <EmployeeNameLink
                        className={LINK_CLASS}
                        to={`/employee/${effort.effortByEmployee.id}`}
                        entity={effort.effortByEmployee}
                    />
                ) : (
                    '-'
                ),
            },
            {
                label: t('efforts.participants'),
                content: linkifyEmployees(effort.employees),
            },
        ],
        [effort.effortByEmployee, effort.employees]
    );
    const [effortBy, participants] = peopleRows;
    const newPeopleRows: Row[] = [
        effortBy,
        {
            ...participants,
            content: '-',
            // By name, as the form orders its values.
            chips: getLinkChips(
                'employee',
                sortByField(
                    (effort.employees ?? []).map(({ employee }) => employee),
                    'name'
                )
            ),
        },
    ];

    // A Rankings referee effort's lines, each directory edition the label of its referees and their statuses. A
    // status saves on change, for whoever may edit the effort or holds the statuses' permission.
    const showsNominations =
        useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE) &&
        effort.effortActivityTypeCode === RANKINGS_REFEREE_TYPE &&
        !!effort.nominations;
    const canUpdateStatuses = (useHasPermissions(STATUS_PERMISSIONS) || !!effort.isEditable) && !effort.isDraft;
    // Each referee's warning, as the form gives it, for whoever may write a BD effort: the endpoint answers no other.
    const canAskWarnings = useHasPermissions(NOMINATION_WARNINGS_PERMISSIONS);
    const warnings = useNominationWarnings(
        {
            effortId: effort.id,
            effortDate: effort.effortDate ?? null,
            lines: effort.nominations ?? [],
        },
        !showsNominations || !canAskWarnings
    );
    const warningText = useNominationWarningText(classifiers['LEGAL_DIRECTORY']);
    const nominationRows: Row[] = (effort.nominations ?? []).map((line, lineIndex) => {
        const lineName = nominationName(
            getClassifierText(classifiers['LEGAL_DIRECTORY'], line.legalDirectoryCode ?? undefined),
            line.editionYear,
            line.practiceArea?.name
        );
        return {
            label: lineName,
            wide: true,
            content: (
                // Capped so a status stays near its referee's name on a wide card.
                <ul className="m-0 flex max-w-2xl list-none flex-col p-0">
                    {line.referees.map(({ id, party, statusCode }) => {
                        const warning = warnings.find((w) => w.lineIndex === lineIndex && w.partyId === party.id);
                        return (
                            <li
                                key={party.id}
                                className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-1"
                            >
                                <div className="flex min-w-0 flex-[1_1_14rem] flex-col gap-1 pt-1">
                                    <RecordLink className={LINK_CLASS} to={`/contact/${party.id}`} label={party.name} />
                                    {warning ? <NominationWarningNote text={warningText(party.name, warning)} /> : null}
                                </div>
                                {id ? (
                                    <NominationStatusControl
                                        id={id}
                                        value={statusCode}
                                        label={t('rankings-referees.status-of', {
                                            name: party.name,
                                            nomination: lineName,
                                        })}
                                        canUpdate={canUpdateStatuses}
                                    />
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            ),
        };
    });
    const shownNominationRows = nominationRows.length > 0 ? nominationRows : [{ label: '', content: '-' }];

    const auditInfo = (
        <AuditInfo
            createdAt={effort.createdAt}
            createdByEmployee={effort.createdByEmployee}
            lastModifiedAt={effort.lastModifiedAt ?? undefined}
            changedByEmployee={effort.changedByEmployee}
        />
    );

    if (isNew) {
        // One line per expense, as stored: its day and who spent it, what it was, and the amount booked.
        // A draft's lines are drafts too, read only through it: no page of their own to link to.
        const expenseRows: Row[] = (effort.expenses ?? []).map((expense) => {
            const what = `${getClassifierText(classifiers['EXPENSE_TYPE'], expense.expenseTypeCode)}${
                expense.description ? `, ${expense.description}` : ''
            }`;
            return {
                label: (
                    <>
                        <FormattedDate value={expense.expenseDate} fallback="" />
                        {expense.employee ? ` · ${expense.employee.name}` : ''}
                    </>
                ),
                content: (
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                        {effort.isDraft ? (
                            <span>{what}</span>
                        ) : (
                            <RecordLink className={LINK_CLASS} to={`/expense/${expense.id}`} label={what} />
                        )}
                        {expense.expenseAmount != null ? (
                            <MoneyDisplay amount={Number(expense.expenseAmount)} currency={expense.currencyTypeCode} />
                        ) : null}
                    </div>
                ),
                wide: true,
            };
        });
        const section = (
            id: 'overview' | 'linked-entities' | 'people' | 'expenses' | 'nominations',
            titleId: string,
            data: Row[]
        ) => (
            <EffortSection id={id} titleId={titleId} effort={effort}>
                {(edit) => (
                    <GridItemList isNew titleId={titleId} data={withMissing(data, missingFields)} button={edit} />
                )}
            </EffortSection>
        );
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {section('overview', 'efforts.overview', newOverviewRows)}
                    {section('linked-entities', 'efforts.linked-entities', newLinkedEntitiesRows)}
                    {showsNominations ? section('nominations', 'efforts.nominations.title', shownNominationRows) : null}
                    {auditInfo}
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {sharedWith}
                    {section('people', 'efforts.people-involved', newPeopleRows)}
                    {section(
                        'expenses',
                        'expenses.expenses',
                        expenseRows.length > 0 ? expenseRows : [{ label: '', content: '-' }]
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-6">
                <GridItemList
                    titleId="efforts.overview"
                    size={12}
                    data={withMissing(overviewRows, missingFields)}
                    key="effort-overview"
                />
                <GridItemList
                    titleId="efforts.linked-entities"
                    size={12}
                    data={withMissing(linkedEntitiesRows, missingFields)}
                    key="effort-linked-entities"
                />
            </div>
            <div className="flex flex-col gap-6">
                {sharedWith}
                <GridItemList
                    titleId="efforts.people-involved"
                    size={12}
                    data={withMissing(peopleRows, missingFields)}
                    key="effort-people"
                />
            </div>
            {showsNominations ? (
                <div className="md:col-span-2">
                    <GridItemList
                        titleId="efforts.nominations.title"
                        size={12}
                        data={shownNominationRows}
                        key="effort-nominations"
                    />
                </div>
            ) : null}
            <div className="md:col-span-2">{auditInfo}</div>
        </div>
    );
};

export default GeneralView;
